import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { User, Mail, Phone, Linkedin, Building2, ArrowLeft, Trash2, Smartphone, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';

const InitialsBadge = ({ initials }) => {
  if (!initials) return null;
  return (
    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded bg-primary/10 text-primary text-[9px] font-bold ml-1.5" data-testid="initials-badge">
      {initials}
    </span>
  );
};

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const contactRes = await axios.get(`${API}/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setContact(contactRes.data);

      const [companyRes, notesRes] = await Promise.all([
        axios.get(`${API}/companies/${contactRes.data.company_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/notes?entity_type=contact&entity_id=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setCompany(companyRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notes`,
        { entity_type: 'contact', entity_id: id, content: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Note ajoutée');
      setNewNote('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Note supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteContact = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact supprimé');
      navigate(company ? `/companies/${company.id}` : '/');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!contact) {
    return (
      <div className="min-h-screen bg-[#f1f5f9]">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(company ? `/companies/${company.id}` : '/')}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Button>

        <div className="bg-white rounded-xl border border-border/50 shadow-sm p-8 mb-6" data-testid="contact-detail">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent rounded-xl">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="contact-name">
                  {contact.name}
                </h1>
                <p className="text-lg text-muted-foreground">{contact.position}</p>
              </div>
            </div>
            <Button
              variant="destructive"
              onClick={handleDeleteContact}
              className="gap-2"
              data-testid="delete-contact-btn"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading font-semibold mb-3">Coordonnées</h3>
              <dl className="space-y-3">
                {contact.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                      {contact.email}
                    </a>
                    <InitialsBadge initials={contact.email_added_by} />
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-3">
                    <PhoneCall className="h-5 w-5 text-muted-foreground" />
                    <a href={`tel:${contact.phone}`} className="hover:underline">
                      Fixe: {contact.phone}
                    </a>
                    <InitialsBadge initials={contact.phone_added_by} />
                  </div>
                )}
                {contact.mobile && (
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-muted-foreground" />
                    <a href={`tel:${contact.mobile}`} className="hover:underline">
                      Portable: {contact.mobile}
                    </a>
                    <InitialsBadge initials={contact.mobile_added_by} />
                  </div>
                )}
                {contact.linkedin_url && (
                  <div className="flex items-center gap-3">
                    <Linkedin className="h-5 w-5 text-muted-foreground" />
                    <a
                      href={contact.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Voir le profil LinkedIn
                    </a>
                  </div>
                )}
              </dl>
            </div>

            {company && (
              <div>
                <h3 className="font-heading font-semibold mb-3">Entreprise</h3>
                <div
                  className="border border-border/50 rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/companies/${company.id}`)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold text-gray-900">{company.name}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">SIREN: {company.siren}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    {company.city}, {company.region}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-border/50">
            <span className="text-sm text-muted-foreground">Source: </span>
            <span className="text-sm font-medium">{contact.source}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/50 shadow-sm p-6">
          <h2 className="text-xl font-heading font-semibold mb-6">Notes et historique</h2>

          <form onSubmit={handleAddNote} className="mb-6">
            <Textarea
              placeholder="Ajouter une note sur ce contact..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="mb-2"
              data-testid="note-input"
            />
            <Button type="submit" className="rounded-full" data-testid="add-note-btn">
              Ajouter la note
            </Button>
          </form>

          <div className="space-y-3" data-testid="notes-list">
            {notes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucune note</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="border border-border/50 rounded-lg p-4" data-testid="note-item">
                  <div className="flex items-start justify-between">
                    <p className="text-sm text-gray-700 flex-1">{note.content}</p>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-destructive hover:text-destructive/80 ml-2"
                      data-testid="delete-note-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(note.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetail;